/**
 * extra.json effect pass chain (W2-01a preview-gl; ARCHITECTURE.md §7.3 step 5, rendering.md R§8-R§9,
 * formats-and-packaging.md P§5.2).
 *
 * Each active effect reads the frame drawn so far and writes the next one (ping-pong between two colour targets):
 *  - non-global effects run after the notes over the letterboxed chart viewport. The destination first receives a
 *    copy of the source (the letterbox bars keep their pixels), then a quad over the viewport runs the shader.
 *    UVScale = viewport size / target size, so `uv` spans exactly the viewport inside the window-sized texture;
 *  - global effects run last over the whole target (UVScale = 1);
 *  - `uv` follows the GL convention, (0, 0) = bottom-left: frames are stored with the image top in the texture's last
 *    row (see gl/glState.ts), so nothing is flipped;
 *  - uniforms, in Phira's order: shader defaults (the `// %default%` comments of custom shaders; the defaults table of
 *    built-ins) -> the effect's vars -> `time` (chart seconds) -> `screenTexture` (unit 0) -> `screenSize` (target
 *    px) -> `UVScale`. A later value of the same uniform wins, as in Phira. Unknown uniform names are ignored.
 *  - every effect starts from zeroed uniforms: Phira gives each effect its own material, so a uniform without a
 *    default and without a var reads 0, never a value another effect with the same shader set earlier;
 *  - a value only reaches a uniform of its own shape (P§5.2 "match the JSON shape to the GLSL type"): a number sets a
 *    `float`, two numbers a `vec2`, four a `vec4`; any other pairing (a vec2 var on a float, a `null` var (0) on a vec4,
 *    anything on an int / bool / vec3 / matrix) is dropped, so the uniform keeps its default (or 0). Phira's uniform
 *    upload drops such a value too (a size mismatch, or a GL type error). Dropped vars are listed in the frame report;
 *  - the vertex stage is fixed (`EFFECT_VERTEX`, GLSL ES 1.00): `uv = (texcoord - 0.5) * UVScale + 0.5`.
 *
 * Shaders: package shaders compile as written, with a default float precision injected when the source sets none at
 * global scope (GLSL ES 1.00 fragment shaders need one; desktop Phira compiles them as highp). Compile / link logs
 * (line numbers mapped back to the file) and bad default comments go to RenderStats.shaderErrors (problem PH-33) and
 * the effect is skipped. Built-ins come from `BUILTIN_SHADERS` (W2-01b); a name missing there is skipped and reported
 * as "not previewed". Programs and failures are cached per source text, so nothing recompiles per frame.
 */

import type { AssetId, EffectId } from '../../../core/model/types';
import type { EffectDraw } from '../../../core/engine/types';
import type { GlState, PxRect } from '../../gl/glState';
import { buildProgram, floatComponents, setUniformValue, type LinkedProgram } from '../../gl/program';
import type { QuadBatch } from '../../gl/quadBatch';
import type { ColorTarget, PingPong } from '../../gl/renderTargets';
import { blitColor } from '../../gl/renderTargets';
import { EFFECT_ATTRIBS, EFFECT_VERTEX } from '../../gl/shaders';
import type { BuiltinShaderDef, TextureProvider } from '../../types';
import { parseUniformDefaults } from './uniformDefaults';

/** Default precision statement injected into package shaders that declare none. */
export const DEFAULT_PRECISION = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif`;

const PRECISION_LINES = DEFAULT_PRECISION.split('\n').length;

/** Compiled effect programs kept (least recently compiled dropped first). */
export const MAX_PROGRAMS = 64;

/**
 * The source with every comment replaced by spaces (line breaks kept, so lines and columns stay where they were), in
 * one pass: a `/*` inside a line comment and a `//` inside a block comment are part of that comment. An unterminated
 * block comment runs to the end.
 */
export function stripComments(src: string): string {
  let out = '';
  let from = 0;
  let i = 0;
  while (i < src.length - 1) {
    if (src[i] !== '/' || (src[i + 1] !== '/' && src[i + 1] !== '*')) {
      i++;
      continue;
    }
    let end: number;
    if (src[i + 1] === '/') {
      const eol = src.indexOf('\n', i);
      end = eol < 0 ? src.length : eol;
    } else {
      const close = src.indexOf('*/', i + 2);
      end = close < 0 ? src.length : close + 2;
    }
    out += src.slice(from, i) + src.slice(i, end).replace(/[^\n]/g, ' ');
    from = end;
    i = end;
  }
  return out + src.slice(from);
}

/** Whether a fragment source sets a default float precision at global scope (outside every `{ }` block). */
export function hasFloatPrecision(src: string): boolean {
  let depth = 0;
  for (const m of stripComments(src).matchAll(/\bprecision\s+(?:lowp|mediump|highp)\s+float\s*;|[{}]/g)) {
    if (m[0] === '{') depth++;
    else if (m[0] === '}') depth = Math.max(0, depth - 1);
    else if (depth === 0) return true;
  }
  return false;
}

/**
 * The source with a default precision injected when it has none: after the leading run of preprocessor directives
 * (`#version` must stay first and `#extension` must precede every statement), at the last point of that run that is
 * outside any `#if` block, so the statement is never conditional. Comments and blank lines are skipped over.
 * `insertedAt` is the 1-based line the injection starts on (0 when nothing was injected).
 */
export function withDefaultPrecision(src: string): {
  readonly source: string;
  readonly insertedAt: number;
  readonly lines: number;
} {
  if (hasFloatPrecision(src)) return { source: src, insertedAt: 0, lines: 0 };
  const lines = src.split('\n');
  let insertAt = 0;
  let depth = 0;
  let inBlock = false;
  for (let i = 0; i < lines.length; i++) {
    const t = (lines[i] ?? '').trim();
    if (inBlock) {
      if (t.includes('*/')) inBlock = false;
      continue;
    }
    if (t === '' || t.startsWith('//')) continue;
    if (t.startsWith('/*')) {
      if (!t.includes('*/')) inBlock = true;
      continue;
    }
    const directive = /^#\s*(\w*)/.exec(t);
    if (directive === null) break;
    const name = directive[1] ?? '';
    if (name === 'if' || name === 'ifdef' || name === 'ifndef') depth++;
    else if (name === 'endif') depth = Math.max(0, depth - 1);
    if (depth === 0) insertAt = i + 1;
  }
  const out = [...lines.slice(0, insertAt), DEFAULT_PRECISION, ...lines.slice(insertAt)].join('\n');
  return { source: out, insertedAt: insertAt + 1, lines: PRECISION_LINES };
}

/** Maps `ERROR: 0:<line>:` positions of a compile log back to the original file's lines. */
export function remapLogLines(log: string, insertedAt: number, injected: number): string {
  if (injected === 0) return log;
  return log.replace(/\b(\d+):(\d+):/g, (whole, file: string, line: string) => {
    const n = Number(line);
    if (!Number.isFinite(n) || n < insertedAt) return whole;
    const orig = n < insertedAt + injected ? insertedAt - 1 : n - injected;
    return `${file}:${String(Math.max(1, orig))}:`;
  });
}

/** A compiled effect shader with its defaults in application order. */
export interface EffectProgram {
  readonly prog: LinkedProgram;
  readonly defaults: ReadonlyArray<readonly [name: string, value: number | readonly number[]]>;
}

type Compiled = { readonly ok: true; readonly program: EffectProgram } | { readonly ok: false; readonly log: string };

/** What happened to the effects of the last frame. */
export interface EffectFrameReport {
  readonly shaderErrors: Array<{ readonly asset: AssetId; readonly log: string }>;
  /** Built-in names without a shader (BUILTIN_SHADERS lacks them): drawn without the effect. */
  readonly notPreviewed: EffectId[];
  /** Effects skipped this frame because their shader source is still loading. */
  readonly pending: EffectId[];
  /** Effects that ran. */
  readonly applied: EffectId[];
  /** Vars of effects that ran whose shape does not match the uniform's GLSL type (dropped, as in Phira). */
  readonly droppedVars: Array<{ readonly effect: EffectId; readonly name: string }>;
}

function emptyReport(): EffectFrameReport {
  return { shaderErrors: [], notPreviewed: [], pending: [], applied: [], droppedVars: [] };
}

/** Uniforms the runner sets itself after the vars (never zeroed first: they are always set). */
const RENDERER_UNIFORMS: ReadonlySet<string> = new Set(['time', 'screenTexture', 'screenSize', 'UVScale']);

/** Number of floats a uniform value carries. */
function valueSize(value: number | readonly number[]): number {
  return typeof value === 'number' ? 1 : value.length;
}

/**
 * Whether Phira's upload of `value` reaches a uniform of GL type `type`: float, vec2 and vec4 uniforms take a value of
 * exactly their size; no value reaches any other type (Phira's vars are floats, vec2s and colours only). An unknown
 * type (0, the driver gave none) takes any value.
 */
export function uniformAccepts(gl: WebGL2RenderingContext, type: number, value: number | readonly number[]): boolean {
  if (type === 0) return true;
  const size = floatComponents(gl, type);
  return (size === 1 || size === 2 || size === 4) && size === valueSize(value);
}

/** How setting a uniform by name went. */
export type UniformOutcome = 'set' | 'inactive' | 'mismatch';

/** Sets a uniform by name when the program has it and the value's shape fits its type (see `uniformAccepts`). */
export function applyUniform(
  gl: WebGL2RenderingContext,
  prog: LinkedProgram,
  name: string,
  value: number | readonly number[],
): UniformOutcome {
  const info = prog.uniforms.get(name);
  const loc = info?.loc ?? prog.uniform(name);
  if (loc === null) return 'inactive';
  const type = info?.type ?? 0;
  if (!uniformAccepts(gl, type, value)) return 'mismatch';
  setUniformValue(gl, loc, type, value);
  return 'set';
}

/** Sets every float uniform of the program to 0, except the ones the runner always sets. */
function zeroUniforms(gl: WebGL2RenderingContext, prog: LinkedProgram): void {
  for (const [name, info] of prog.uniforms) {
    if (RENDERER_UNIFORMS.has(name) || floatComponents(gl, info.type) === 0) continue;
    setUniformValue(gl, info.loc, info.type, 0);
  }
}

/** Geometry of one pass: the target size and, for non-global effects, the viewport inside it (target px, y down). */
export interface EffectPassGeometry {
  readonly targetW: number;
  readonly targetH: number;
  /** Null = the whole target (global effects). */
  readonly viewport: PxRect | null;
}

export class EffectRunner {
  private readonly gl: WebGL2RenderingContext;
  private readonly state: GlState;
  private readonly batch: QuadBatch;
  private readonly builtins: () => readonly BuiltinShaderDef[];
  private readonly compiled = new Map<string, Compiled>();
  private report: EffectFrameReport = emptyReport();

  constructor(
    gl: WebGL2RenderingContext,
    state: GlState,
    batch: QuadBatch,
    builtins: () => readonly BuiltinShaderDef[],
  ) {
    this.gl = gl;
    this.state = state;
    this.batch = batch;
    this.builtins = builtins;
  }

  beginFrame(): void {
    this.report = emptyReport();
  }

  get frameReport(): EffectFrameReport {
    return this.report;
  }

  private compile(key: string, fragment: string, defaults: () => EffectProgram['defaults'] | string): Compiled {
    const known = this.compiled.get(key);
    if (known !== undefined) return known;
    let result: Compiled;
    const defs = defaults();
    if (typeof defs === 'string') result = { ok: false, log: defs };
    else {
      const prepared = withDefaultPrecision(fragment);
      const r = buildProgram(this.gl, EFFECT_VERTEX, prepared.source, EFFECT_ATTRIBS);
      result = r.ok
        ? { ok: true, program: { prog: r.prog, defaults: defs } }
        : { ok: false, log: `${r.stage}: ${remapLogLines(r.log, prepared.insertedAt, prepared.lines)}` };
    }
    this.compiled.set(key, result);
    // Bounded: edited package shaders leave old sources behind.
    for (const [oldKey, old] of this.compiled) {
      if (this.compiled.size <= MAX_PROGRAMS) break;
      if (old.ok) this.gl.deleteProgram(old.program.prog.program);
      this.compiled.delete(oldKey);
    }
    return result;
  }

  /** The program of an effect, or null (skipped; the reason is in the frame report). */
  resolve(e: EffectDraw, textures: TextureProvider | null): EffectProgram | null {
    const shader = e.shader;
    if (shader.kind === 'builtin') {
      const def = this.builtins().find((b) => b.name === shader.name);
      if (def === undefined) {
        this.report.notPreviewed.push(e.effect);
        return null;
      }
      const c = this.compile(`builtin\u0000${def.name}\u0000${def.fragment}`, def.fragment, () =>
        Object.entries(def.defaults).map(([k, v]) => [k, v] as const),
      );
      if (!c.ok) {
        // A broken built-in is our bug, not the chart's: draw without it and say so.
        this.report.notPreviewed.push(e.effect);
        return null;
      }
      return c.program;
    }
    const src = textures?.shaderSource(shader.asset) ?? null;
    if (src === null) {
      const failure = textures?.failure?.(shader.asset, 'shader') ?? null;
      if (failure !== null) this.report.shaderErrors.push({ asset: shader.asset, log: `load: ${failure}` });
      else this.report.pending.push(e.effect);
      return null;
    }
    const c = this.compile(`asset\u0000${src}`, src, () => {
      const parsed = parseUniformDefaults(src);
      if (parsed.errors.length > 0) return `defaults: ${parsed.errors.map((x) => x.detail).join('; ')}`;
      return parsed.defaults.map((d) => [d.name, d.value] as const);
    });
    if (!c.ok) {
      this.report.shaderErrors.push({ asset: shader.asset, log: c.log });
      return null;
    }
    return c.program;
  }

  /** Runs one effect: reads `src`, writes into the bound target. */
  draw(p: EffectProgram, e: EffectDraw, src: ColorTarget, g: EffectPassGeometry): void {
    const gl = this.gl;
    const prog = p.prog;
    this.batch.flush();
    this.state.useProgram(prog.program);
    zeroUniforms(gl, prog);
    for (const [name, value] of p.defaults) applyUniform(gl, prog, name, value);
    for (const [name, value] of e.uniforms) {
      if (applyUniform(gl, prog, name, value) === 'mismatch') this.report.droppedVars.push({ effect: e.effect, name });
    }
    applyUniform(gl, prog, 'time', e.time);
    const sampler = prog.uniform('screenTexture');
    if (sampler !== null) gl.uniform1i(sampler, 0);
    applyUniform(gl, prog, 'screenSize', [g.targetW, g.targetH]);
    const vp = g.viewport ?? { x: 0, y: 0, w: g.targetW, h: g.targetH };
    applyUniform(gl, prog, 'UVScale', [vp.w / g.targetW, vp.h / g.targetH]);
    // Clip-space quad over the viewport; texcoord (0, 0) at its bottom-left corner.
    const x0 = (2 * vp.x) / g.targetW - 1;
    const x1 = (2 * (vp.x + vp.w)) / g.targetW - 1;
    const yTop = 1 - (2 * vp.y) / g.targetH;
    const yBottom = 1 - (2 * (vp.y + vp.h)) / g.targetH;
    const b = this.batch;
    b.setProgram(prog);
    b.setTexture(src.tex);
    b.setBlend('none');
    b.quad(x0, yBottom, 0, 0, x1, yBottom, 1, 0, x1, yTop, 1, 1, x0, yTop, 0, 1, 255, 255, 255, 255);
    b.flush();
    this.report.applied.push(e.effect);
  }

  /**
   * Runs a list of effects over `from` using the ping-pong pair and returns the target holding the result (`from`
   * itself when no effect ran). `from` must be a member of `pp`.
   */
  chain(
    effects: readonly EffectDraw[],
    from: ColorTarget,
    pp: PingPong,
    g: EffectPassGeometry,
    textures: TextureProvider | null,
  ): ColorTarget {
    let cur = from;
    for (const e of effects) {
      const p = this.resolve(e, textures);
      if (p === null) continue;
      const dst = pp.other(cur);
      if (g.viewport !== null) {
        this.state.setScissor(null);
        blitColor(this.gl, cur.fbo, dst.fbo, g.targetW, g.targetH);
        this.state.forgetTarget();
      }
      this.state.bindTarget(dst.fbo, g.targetW, g.targetH);
      this.draw(p, e, cur, g);
      cur = dst;
    }
    return cur;
  }

  /** Forgets compiled programs (context lost: they are gone; dispose: delete them). */
  reset(deletePrograms: boolean): void {
    if (deletePrograms) {
      for (const c of this.compiled.values()) if (c.ok) this.gl.deleteProgram(c.program.prog.program);
    }
    this.compiled.clear();
  }

  dispose(): void {
    this.reset(true);
  }
}
